import { useRef, useEffect, useState, useCallback } from "react";
import "./index.css";

// --- Constants ---
const PARTICLE_RADIUS = 3;
const BOLTZMANN = 1; // Simplified constant for simulation
const PARTICLE_MASS = 1;
const BASE_PARTICLE_COUNT = 200;

interface Particle {
	x: number;
	y: number;
	vx: number;
	vy: number;
	color: string;
}

function speedToColor(speed: number): string {
	// Map speed to a blue -> cyan -> green -> yellow -> red gradient
	const maxSpeed = 12;
	const t = Math.min(speed / maxSpeed, 1);
	if (t < 0.25) {
		const s = t / 0.25;
		const r = Math.round(30);
		const g = Math.round(100 + s * 155);
		const b = Math.round(220 + s * 35);
		return `rgb(${r},${g},${b})`;
	} else if (t < 0.5) {
		const s = (t - 0.25) / 0.25;
		const r = Math.round(30 + s * 200);
		const g = Math.round(255);
		const b = Math.round(255 - s * 155);
		return `rgb(${r},${g},${b})`;
	} else if (t < 0.75) {
		const s = (t - 0.5) / 0.25;
		const r = Math.round(230 + s * 25);
		const g = Math.round(255 - s * 80);
		const b = Math.round(100 - s * 80);
		return `rgb(${r},${g},${b})`;
	} else {
		const s = (t - 0.75) / 0.25;
		const r = Math.round(255);
		const g = Math.round(175 - s * 135);
		const b = Math.round(20);
		return `rgb(${r},${g},${b})`;
	}
}

function createParticles(
	count: number,
	temperature: number,
	boxX: number,
	boxY: number,
	boxW: number,
	boxH: number
): Particle[] {
	const speed = Math.sqrt((BOLTZMANN * temperature) / PARTICLE_MASS);
	const particles: Particle[] = [];
	for (let i = 0; i < count; i++) {
		const angle = Math.random() * 2 * Math.PI;
		// Maxwell-Boltzmann-like distribution using Box-Muller
		const u1 = Math.random();
		const u2 = Math.random();
		const mag = speed * Math.sqrt(-2 * Math.log(u1)) * 0.7;
		const vx = mag * Math.cos(angle);
		const vy = mag * Math.sin(angle);
		const s = Math.sqrt(vx * vx + vy * vy);
		particles.push({
			x: boxX + PARTICLE_RADIUS + Math.random() * (boxW - 2 * PARTICLE_RADIUS),
			y: boxY + PARTICLE_RADIUS + Math.random() * (boxH - 2 * PARTICLE_RADIUS),
			vx,
			vy,
			color: speedToColor(s),
		});
	}
	return particles;
}

export function App() {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const particlesRef = useRef<Particle[]>([]);
	const animFrameRef = useRef<number>(0);
	const wallHitsRef = useRef<number>(0);
	const wallHitsWindowRef = useRef<number[]>([]);
	const pressureRef = useRef<number>(0);

	const [temperature, setTemperature] = useState(5);
	const [volume, setVolume] = useState(0.7); // 0.2 to 1.0
	const [particleCount, setParticleCount] = useState(BASE_PARTICLE_COUNT);
	const [paused, setPaused] = useState(false);
	const [showTrails, setShowTrails] = useState(false);
	const [pressure, setPressure] = useState(0);
	const [avgSpeed, setAvgSpeed] = useState(0);

	const pausedRef = useRef(paused);
	const tempRef = useRef(temperature);
	const volumeRef = useRef(volume);
	const showTrailsRef = useRef(showTrails);

	useEffect(() => {
		pausedRef.current = paused;
	}, [paused]);
	useEffect(() => {
		tempRef.current = temperature;
	}, [temperature]);
	useEffect(() => {
		volumeRef.current = volume;
	}, [volume]);
	useEffect(() => {
		showTrailsRef.current = showTrails;
	}, [showTrails]);

	const getBoxDimensions = useCallback(
		(w: number, h: number) => {
			const padding = 20;
			const maxW = w - padding * 2;
			const maxH = h - padding * 2;
			const boxW = maxW * volumeRef.current;
			const boxH = maxH * volumeRef.current;
			const boxX = (w - boxW) / 2;
			const boxY = (h - boxH) / 2;
			return { boxX, boxY, boxW, boxH };
		},
		[]
	);

	// Rescale velocities when temperature changes
	useEffect(() => {
		const particles = particlesRef.current;
		if (particles.length === 0) return;

		const targetSpeed = Math.sqrt((BOLTZMANN * temperature) / PARTICLE_MASS);
		let totalSpeed = 0;
		for (const p of particles) {
			totalSpeed += Math.sqrt(p.vx * p.vx + p.vy * p.vy);
		}
		const currentAvg = totalSpeed / particles.length;
		if (currentAvg < 0.001) return;
		const scale = targetSpeed / currentAvg;

		for (const p of particles) {
			p.vx *= scale;
			p.vy *= scale;
			const s = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
			p.color = speedToColor(s);
		}
	}, [temperature]);

	// Re-create particles when count changes
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const { boxX, boxY, boxW, boxH } = getBoxDimensions(
			canvas.width,
			canvas.height
		);
		particlesRef.current = createParticles(
			particleCount,
			tempRef.current,
			boxX,
			boxY,
			boxW,
			boxH
		);
	}, [particleCount, getBoxDimensions]);

	// Constrain particles when volume shrinks
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const { boxX, boxY, boxW, boxH } = getBoxDimensions(
			canvas.width,
			canvas.height
		);
		for (const p of particlesRef.current) {
			p.x = Math.max(boxX + PARTICLE_RADIUS, Math.min(boxX + boxW - PARTICLE_RADIUS, p.x));
			p.y = Math.max(boxY + PARTICLE_RADIUS, Math.min(boxY + boxH - PARTICLE_RADIUS, p.y));
		}
	}, [volume, getBoxDimensions]);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d")!;

		const resize = () => {
			canvas.width = window.innerWidth;
			canvas.height = window.innerHeight;
		};
		resize();
		window.addEventListener("resize", resize);

		// Init particles
		const { boxX, boxY, boxW, boxH } = getBoxDimensions(
			canvas.width,
			canvas.height
		);
		if (particlesRef.current.length === 0) {
			particlesRef.current = createParticles(
				particleCount,
				tempRef.current,
				boxX,
				boxY,
				boxW,
				boxH
			);
		}

		let lastStatsUpdate = 0;

		const loop = (time: number) => {
			animFrameRef.current = requestAnimationFrame(loop);
			const w = canvas.width;
			const h = canvas.height;
			const {
				boxX: bx,
				boxY: by,
				boxW: bw,
				boxH: bh,
			} = getBoxDimensions(w, h);
			const particles = particlesRef.current;

			if (!pausedRef.current) {
				let frameWallHits = 0;

				// Physics step
				for (const p of particles) {
					p.x += p.vx;
					p.y += p.vy;

					// Wall collisions
					if (p.x - PARTICLE_RADIUS < bx) {
						p.x = bx + PARTICLE_RADIUS;
						p.vx = Math.abs(p.vx);
						frameWallHits++;
					} else if (p.x + PARTICLE_RADIUS > bx + bw) {
						p.x = bx + bw - PARTICLE_RADIUS;
						p.vx = -Math.abs(p.vx);
						frameWallHits++;
					}
					if (p.y - PARTICLE_RADIUS < by) {
						p.y = by + PARTICLE_RADIUS;
						p.vy = Math.abs(p.vy);
						frameWallHits++;
					} else if (p.y + PARTICLE_RADIUS > by + bh) {
						p.y = by + bh - PARTICLE_RADIUS;
						p.vy = -Math.abs(p.vy);
						frameWallHits++;
					}
				}

				wallHitsRef.current += frameWallHits;
				wallHitsWindowRef.current.push(frameWallHits);
				if (wallHitsWindowRef.current.length > 60) {
					wallHitsRef.current -= wallHitsWindowRef.current.shift()!;
				}

				// Particle-particle collisions (spatial hashing for performance)
				const cellSize = PARTICLE_RADIUS * 4;
				const cols = Math.ceil(bw / cellSize);
				const grid = new Map<number, number[]>();

				for (let i = 0; i < particles.length; i++) {
					const p = particles[i];
					const cx = Math.floor((p.x - bx) / cellSize);
					const cy = Math.floor((p.y - by) / cellSize);
					const key = cy * cols + cx;
					if (!grid.has(key)) grid.set(key, []);
					grid.get(key)!.push(i);
				}

				for (const [key, indices] of grid) {
					const cy = Math.floor(key / cols);
					const cx = key - cy * cols;
					const neighbors: number[] = [...indices];
					for (let dy = -1; dy <= 1; dy++) {
						for (let dx = -1; dx <= 1; dx++) {
							if (dx === 0 && dy === 0) continue;
							const nk = (cy + dy) * cols + (cx + dx);
							if (grid.has(nk)) neighbors.push(...grid.get(nk)!);
						}
					}
					for (let a = 0; a < indices.length; a++) {
						const i = indices[a];
						const p1 = particles[i];
						for (let b = a + 1; b < neighbors.length; b++) {
							const j = neighbors[b];
							if (j <= i) continue;
							const p2 = particles[j];
							const dx = p2.x - p1.x;
							const dy = p2.y - p1.y;
							const distSq = dx * dx + dy * dy;
							const minDist = PARTICLE_RADIUS * 2;
							if (distSq < minDist * minDist && distSq > 0.0001) {
								const dist = Math.sqrt(distSq);
								const nx = dx / dist;
								const ny = dy / dist;
								const dvx = p1.vx - p2.vx;
								const dvy = p1.vy - p2.vy;
								const dvDotN = dvx * nx + dvy * ny;
								if (dvDotN > 0) {
									p1.vx -= dvDotN * nx;
									p1.vy -= dvDotN * ny;
									p2.vx += dvDotN * nx;
									p2.vy += dvDotN * ny;
									// Separate overlapping particles
									const overlap = minDist - dist;
									p1.x -= (overlap / 2) * nx;
									p1.y -= (overlap / 2) * ny;
									p2.x += (overlap / 2) * nx;
									p2.y += (overlap / 2) * ny;
								}
							}
						}
					}
				}

				// Update colors based on speed
				for (const p of particles) {
					const s = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
					p.color = speedToColor(s);
				}
			}

			// Update stats every ~500ms
			if (time - lastStatsUpdate > 500) {
				lastStatsUpdate = time;
				const perimeter = 2 * (bw + bh);
				const pres = perimeter > 0 ? wallHitsRef.current / (perimeter / 100) : 0;
				pressureRef.current = pres;
				setPressure(Math.round(pres * 10) / 10);

				let totalSpd = 0;
				for (const p of particles) {
					totalSpd += Math.sqrt(p.vx * p.vx + p.vy * p.vy);
				}
				setAvgSpeed(
					Math.round((totalSpd / Math.max(particles.length, 1)) * 100) / 100
				);
			}

			// --- Render ---
			if (showTrailsRef.current) {
				ctx.fillStyle = "rgba(0, 0, 0, 0.15)";
			} else {
				ctx.fillStyle = "#000000";
			}
			ctx.fillRect(0, 0, w, h);

			// Box border with glow
			const borderGlow = Math.min(pressureRef.current / 15, 1);
			const glowR = Math.round(80 + borderGlow * 175);
			const glowG = Math.round(140 + (1 - borderGlow) * 60);
			const glowB = Math.round(220 - borderGlow * 140);
			ctx.strokeStyle = `rgb(${glowR}, ${glowG}, ${glowB})`;
			ctx.lineWidth = 2;
			ctx.shadowColor = `rgba(${glowR}, ${glowG}, ${glowB}, 0.6)`;
			ctx.shadowBlur = 10 + borderGlow * 20;
			ctx.strokeRect(bx, by, bw, bh);
			ctx.shadowBlur = 0;

			// Interior subtle gradient
			const grad = ctx.createRadialGradient(
				bx + bw / 2,
				by + bh / 2,
				0,
				bx + bw / 2,
				by + bh / 2,
				Math.max(bw, bh) / 2
			);
			grad.addColorStop(0, "rgba(30, 40, 70, 0.15)");
			grad.addColorStop(1, "rgba(10, 15, 30, 0.05)");
			ctx.fillStyle = grad;
			ctx.fillRect(bx, by, bw, bh);

			// Particles
			for (const p of particles) {
				ctx.beginPath();
				ctx.arc(p.x, p.y, PARTICLE_RADIUS, 0, Math.PI * 2);
				ctx.fillStyle = p.color;
				ctx.fill();
			}

			// Corner accents
			const cornerLen = 15;
			ctx.strokeStyle = `rgba(${glowR}, ${glowG}, ${glowB}, 0.8)`;
			ctx.lineWidth = 3;
			ctx.shadowBlur = 0;
			// Top-left
			ctx.beginPath();
			ctx.moveTo(bx, by + cornerLen);
			ctx.lineTo(bx, by);
			ctx.lineTo(bx + cornerLen, by);
			ctx.stroke();
			// Top-right
			ctx.beginPath();
			ctx.moveTo(bx + bw - cornerLen, by);
			ctx.lineTo(bx + bw, by);
			ctx.lineTo(bx + bw, by + cornerLen);
			ctx.stroke();
			// Bottom-left
			ctx.beginPath();
			ctx.moveTo(bx, by + bh - cornerLen);
			ctx.lineTo(bx, by + bh);
			ctx.lineTo(bx + cornerLen, by + bh);
			ctx.stroke();
			// Bottom-right
			ctx.beginPath();
			ctx.moveTo(bx + bw - cornerLen, by + bh);
			ctx.lineTo(bx + bw, by + bh);
			ctx.lineTo(bx + bw, by + bh - cornerLen);
			ctx.stroke();
		};

		animFrameRef.current = requestAnimationFrame(loop);

		return () => {
			cancelAnimationFrame(animFrameRef.current);
			window.removeEventListener("resize", resize);
		};
	}, [getBoxDimensions, particleCount]);

	// Ideal gas law: PV = NkT
	const idealPressure =
		Math.round(((particleCount * BOLTZMANN * temperature) / (volume * 100)) * 10) / 10;

	return (
		<div className="w-full h-screen relative overflow-hidden bg-black">
			<canvas
				ref={canvasRef}
				className="absolute inset-0 w-full h-full"
			/>

			{/* Title */}
			<div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none select-none">
				<h1 className="text-2xl font-bold text-white/90 tracking-wide">
					Gas in a Box
				</h1>
				<p className="text-xs text-white/40 text-center tracking-widest uppercase">
					Ideal Gas Law Simulation
				</p>
			</div>

			{/* Stats Panel - Top Right */}
			<div className="absolute top-4 right-4 z-10 bg-black/60 backdrop-blur-md rounded-lg border border-white/10 p-4 min-w-[200px]">
				<h2 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">
					Measurements
				</h2>
				<div className="space-y-2 text-sm">
					<div className="flex justify-between">
						<span className="text-white/50">Pressure (measured)</span>
						<span className="text-cyan-400 font-mono">{pressure}</span>
					</div>
					<div className="flex justify-between">
						<span className="text-white/50">Pressure (PV=NkT)</span>
						<span className="text-emerald-400 font-mono">{idealPressure}</span>
					</div>
					<div className="flex justify-between">
						<span className="text-white/50">Avg Speed</span>
						<span className="text-yellow-400 font-mono">{avgSpeed}</span>
					</div>
					<div className="flex justify-between">
						<span className="text-white/50">Particles</span>
						<span className="text-white/80 font-mono">{particleCount}</span>
					</div>
					<div className="flex justify-between">
						<span className="text-white/50">Volume</span>
						<span className="text-white/80 font-mono">
							{Math.round(volume * 100)}%
						</span>
					</div>
				</div>
				{/* Speed Legend */}
				<div className="mt-3 pt-3 border-t border-white/10">
					<div className="text-xs text-white/40 mb-1">Speed</div>
					<div
						className="h-2 rounded-full"
						style={{
							background:
								"linear-gradient(to right, rgb(30,100,220), rgb(30,255,255), rgb(230,255,100), rgb(255,175,20), rgb(255,40,20))",
						}}
					/>
					<div className="flex justify-between text-[10px] text-white/30 mt-0.5">
						<span>Slow</span>
						<span>Fast</span>
					</div>
				</div>
			</div>

			{/* Controls Panel - Bottom */}
			<div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-black/60 backdrop-blur-md rounded-lg border border-white/10 p-4 flex flex-wrap items-center gap-6">
				{/* Temperature */}
				<div className="flex flex-col items-center gap-1">
					<label className="text-xs text-white/50 uppercase tracking-wider">
						Temperature
					</label>
					<input
						type="range"
						min="0.5"
						max="20"
						step="0.5"
						value={temperature}
						onChange={(e) => setTemperature(parseFloat(e.target.value))}
						className="w-32 accent-orange-500"
					/>
					<span className="text-xs text-orange-400 font-mono">{temperature} K</span>
				</div>

				{/* Volume */}
				<div className="flex flex-col items-center gap-1">
					<label className="text-xs text-white/50 uppercase tracking-wider">
						Volume
					</label>
					<input
						type="range"
						min="0.2"
						max="1"
						step="0.05"
						value={volume}
						onChange={(e) => setVolume(parseFloat(e.target.value))}
						className="w-32 accent-blue-500"
					/>
					<span className="text-xs text-blue-400 font-mono">
						{Math.round(volume * 100)}%
					</span>
				</div>

				{/* Particle Count */}
				<div className="flex flex-col items-center gap-1">
					<label className="text-xs text-white/50 uppercase tracking-wider">
						Molecules
					</label>
					<input
						type="range"
						min="20"
						max="500"
						step="10"
						value={particleCount}
						onChange={(e) => setParticleCount(parseInt(e.target.value))}
						className="w-32 accent-green-500"
					/>
					<span className="text-xs text-green-400 font-mono">{particleCount}</span>
				</div>

				{/* Divider */}
				<div className="w-px h-10 bg-white/10" />

				{/* Trails Toggle */}
				<button
					onClick={() => setShowTrails(!showTrails)}
					className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
						showTrails
							? "bg-purple-500/30 text-purple-300 border border-purple-500/50"
							: "bg-white/5 text-white/40 border border-white/10 hover:bg-white/10"
					}`}
				>
					Trails {showTrails ? "ON" : "OFF"}
				</button>

				{/* Pause/Play */}
				<button
					onClick={() => setPaused(!paused)}
					className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
						paused
							? "bg-red-500/30 text-red-300 border border-red-500/50"
							: "bg-white/5 text-white/40 border border-white/10 hover:bg-white/10"
					}`}
				>
					{paused ? "PAUSED" : "PAUSE"}
				</button>

				{/* Reset */}
				<button
					onClick={() => {
						setTemperature(5);
						setVolume(0.7);
						setParticleCount(BASE_PARTICLE_COUNT);
						setShowTrails(false);
						setPaused(false);
					}}
					className="px-3 py-1.5 rounded text-xs font-medium bg-white/5 text-white/40 border border-white/10 hover:bg-white/10 transition-colors"
				>
					RESET
				</button>
			</div>

			{/* Formula Display - Bottom Left */}
			<div className="absolute bottom-4 left-4 z-10 bg-black/40 backdrop-blur-sm rounded-lg border border-white/5 px-3 py-2">
				<div className="text-white/30 text-xs font-mono">
					PV = NkT
				</div>
				<div className="text-white/20 text-[10px] mt-1">
					{particleCount} x {BOLTZMANN} x {temperature} = {Math.round(particleCount * BOLTZMANN * temperature)}
				</div>
			</div>
		</div>
	);
}

export default App;
